import { ClientMessageSender } from './clientMessageSender.js';
import { CommonSupportedCommands } from '@/host/api-client/CommonSupportedCommands.js';

// Threshold for considering an image to be spanning the viewport (95%)
const VIEWPORT_COVERAGE_THRESHOLD = 0.95;

// Threshold for considering text to be significant (num chars)
const SIGNIFICANT_TEXT_THRESHOLD = 75;

// 1P image template ads generally have this class name
const AD_BACKGROUND_IMAGE_CLASS = 'ad-background-image';

/**
 * Checks if an image has the 'ad-background-image' class
 */
export const hasBackgroundImageClass = (imgElement: HTMLImageElement): boolean =>
    imgElement.classList.contains(AD_BACKGROUND_IMAGE_CLASS);

/**
 * Checks if an image covers a significant portion of the viewport width
 */
export const isImageCoveringViewportWidth = (imgWidth: number): boolean =>
    imgWidth / window.innerWidth >= VIEWPORT_COVERAGE_THRESHOLD;

/**
 * Checks if the document has significant text content
 */
export const hasSignificantText = (): boolean => {
    // get innerText and strip whitespace
    const text = (document.documentElement.innerText ?? '').replace(/\s/g, '');
    return text.length > SIGNIFICANT_TEXT_THRESHOLD;
};

/**
 * Checks an array of images for one with the target className
 * @param images
 * @returns
 */
export const hasAnyImageWithAdBackgroundClass = (images: HTMLImageElement[]) =>
    images.some((image) => image.classList.contains(AD_BACKGROUND_IMAGE_CLASS));

/**
 * Returns the largest image based on area.
 */
export const hasLargeImage = (images: HTMLImageElement[]): boolean => {
    for (const image of images) {
        const rect = image.getBoundingClientRect();

        // check to see if this image covers all/most of the viewport width
        if (isImageCoveringViewportWidth(rect.width)) {
            return true;
        }
    }

    return false;
};

/**
 * Determines if the ad is an image template ad and sends appropriate message if needed
 */
export const isImageTemplateAd = (): boolean => {
    // Find all images in the creative
    const images = Array.from(document.querySelectorAll('img'));

    // no images in the ad means it is not an image template
    if (!images.length) return false;

    // if any image has .ad-background-class, it is an image template
    if (hasAnyImageWithAdBackgroundClass(images)) return true;

    // if no large images (spans viewport width), it is not an image template
    if (!hasLargeImage(images)) return false;

    // if the ad has a full width image, but has a lot of text, likely not an image template
    if (hasSignificantText()) return false;

    // all conditions met, this is likely an image-only ad
    return true;
};

/**
 * Checks to see if the creative consists primarily of an image that spans (or nearly spans)
 * the entire viewport. If so, sends a message to the host to indicate this is an image-only creative.
 */
export const checkAndResizeImageOnlyCreative = (cms: ClientMessageSender): void => {
    // Check if the creative is an image-only creative
    if (isImageTemplateAd()) {
        // If we found a large image and no significant text
        cms.sendMessage<CommonSupportedCommands['resizeForImageOnlyTemplate']>('resizeForImageOnlyTemplate');
    }
};
